/** @module csi/widget/Container */
define([
	"dojo/_base/declare",
	"dijit/_Container",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"dijit/_WidgetsInTemplateMixin",
	"dijit/form/Button",
	"dijit/registry",
	"csi/_base/underscore",
	"csi/html",
	"dojo/text!./Container.html"
], function(declare,_Container,_TemplatedMixin,_WidgetBase,_WidgetsInTemplateMixin,Button,registry,_,html,template) {

	// TODO: This might not support async stores if store is set while still being queried

	return declare([_WidgetBase,_TemplatedMixin,_WidgetsInTemplateMixin,_Container], {
		baseClass: "csi-container",
		templateString: template,

		// extension points
		containerNode: null,
		newNode: null,

		/** @proeprty {boolean} allowDelete - set to true to render button to allow items to be deleted; can only be set on creation */
		allowDelete: false,

		/** @proeprty {boolean} allowNew - set to true to render button to allow creation of new items; can only be set on creation */
		allowNew: false,

		/** @property {Object} deleteButtonParams - parameters to use to construct delete buttons for each item */
		deleteButtonParams: {
			"class": html.CSS_BUTTON_SECONDARY,
			materialIcon: "close",
			showLabel: false
		},

		/** @property {boolean} deleteOnHover - set to true if delete button should only show on hover of item */
		deleteOnHover: false,
		
		/** @property {String} disabledProperty - set to name of property to check on child items to determine if they are disabled */
		disabledProperty: null,

		/** @property {Object} newButtonParams - parameters to use to construct new button */
		newButtonParams: {
			label: "New"
		},

		/** @property {Store} store - store used to query for items */
		store: null,

		/** @property {_WidgetBase} widgetClass - class to use to render each store item; item property will be passed to widget */
		widgetClass: null,

		/** @property {string} widgetDeleteNode - property on widget where to render delete widget */
		widgetDeleteNode: "deleteNode",

		/** @property {string[]} [widgetProperties] - list of properties to extract from store item to set on widget */
		widgetProperties: null,

		/** @property {Object} [widgetParams] - extra parameters to use to create each widget */
		widgetParams: null,

		/**
		 * Return true if item should be deleted when changed
		 * @param {Object} item
		 * @return {boolean}
		 */
		deleteOnChange: function(item) {
			return false;
		},
		
		/**
		 * Return the child widget by index
		 * @param {number} index
		 * @return {Widget|null}
		 */
		getChildByIndex: function(index) {
			if (!_.isNumber(index)) {
				return null
			}
			
			return this.getChildren()[index];
		},

		/**
		 * Extension point called when new item added
		 * @return {Object}
		 */
		onNewItem: function() {
			var item = {};
			item[this.store.idProperty] = _.uniqueId();
			return item;
		},

		/**
		 * Extension point called when item is changed
		 * @param {Object} item
		 */
		onChangeItem: function(item) { },

		/**
		 * Extension point called when item is deleted
		 * @param {Object} item
		 */
		onDeleteItem: function(item) { },
		
		/**
		 * Extension point called when new item added
		 * @param {Object} item
		 * @param {_WidgetBase} widget
		 */
		onRenderNewItem: function(item, widget) { },

		/**
		 * Extension point to initialize parameters set on widget when instantiated
		 * @param {Object} item
		 * @param {Object} params
		 */
		onWidgetParams: function(item, params) { },

		/**
		 * Redraw / rerender container
		 */
		redraw: function() {
			this._render();
		},

		/**
		 * Render items in store on startup
		 */
		startup: function() {
			if (this._started) {
				return;
			}

			this._render();

			// construct New button if allowed
			if (this.allowNew) {
				var params = _.clone(this.newButtonParams);
				params["class"] = params["class"] || html.CSS_BUTTON_SECONDARY;
				params.onClick = _.bind(this._onClickNew, this);
				new Button(params).placeAt(this.newNode).startup();
			}

			this.inherited(arguments);
		},

		/**
		 * Delete item from store
		 * @param {Object} item
		 */
		_onClickDelete: function(item, widget) {
			this.removeChild(widget);
			widget.destroy();
			if (item) {
				this.store.remove(this.store.getIdentity(item));
				this.onDeleteItem(item);
			}
		},

		/**
		 * Add new item to store
		 */
		_onClickNew: function() {
			console.assert(!!this.store);
			var item = this.onNewItem();
			var w = this._renderItem(item);
			this.onRenderNewItem(item, w);
		},

		/**
		 * Clear DOM and render all store items
		 */
		_render: function() {
			html.clear(this.containerNode, true);

			if (this.store) {
				this.store.query().forEach(this._renderItem, this);
			}
		},

		/**
		 * Create child widget for item and add to container
		 * @param {Object} item
		 * @param {number} index
		 */
		_renderItem: function(item, index) {
			var self = this;
			var id = this.store.getIdentity(item);
			var w;

			var params = {
				disabled: this.disabled,
				index: index,
				item: item
			};

			if (this.widgetParams) {
				_.extend(params, this.widgetParams);
			}

			if (this.widgetProperties) {
				_.extend(params, _.pick(item, this.widgetProperties));
			}

			this.onWidgetParams(item, params);

			// if onChange function not provided, then add it
			if (!params.onChange) {
				params.onChange = function() {
					// when item changes, compose new item and call extension point
					var newItem = _.clone(item);

					_.each(self.widgetProperties, function(prop) {
						newItem[prop] = w.get(prop);
					});

					if (self.deleteOnChange(newItem)) {
						self._onClickDelete(self.store.get(id), w);
					} else {
						self.store.put(newItem);
						self.onChangeItem(newItem);
					}
				};
			}

			w = new this.widgetClass(params);
			$(w.domNode).addClass(this.baseClass + "-item");

			// add delete button if allowed and placeholder exists
			if (this.allowDelete) {
				var domNode = w[this.widgetDeleteNode];
				if (domNode) {
					params = _.clone(this.deleteButtonParams);
					params.onClick = function() {
						self._onClickDelete(self.store.get(id), w);
					};
					new Button(params).placeAt(domNode).startup();
					$(domNode).addClass(this.baseClass + "-delete");
				}
			}

			this.addChild(w);
			return w;
		},

		/**
		 * Add CSS indicating if delete button should only show on hover
		 */
		_setDeleteOnHoverAttr: {node: "domNode", type: "toggleClass"},

		/**
		 * Update disabled status of container and all children
		 * @param {boolean} disabled
		 */
		_setDisabledAttr: function(value) {
			var disabledProperty = this.disabledProperty;
			this._set("disabled", value);
			$(this.domNode).toggleClass(this.baseClass + "-disabled", value);

			_.each(this.getChildren(), function(child) {
				var itemDisabled = value;

				if (!value && disabledProperty) {
					itemDisabled = !!child.item[disabledProperty];
				}

				child.set("disabled", itemDisabled);
				
				var deleteNode = child.deleteNode;
				if (deleteNode) {
					var deleteButton = registry.byNode(deleteNode.firstChild);
					if (deleteButton) {
						deleteButton.set("disabled", itemDisabled);
					}
				}
			});
		},

		/**
		 * Render store items when changed
		 * @param {Store} value
		 */
		_setStoreAttr: function(value) {
			this._set("store", value);

			if (this._started) {
				this._render();
			}
		}
	});
});